import React, { useState, useEffect } from 'react';
import { UserPlus, ArrowLeft, Check, AlertCircle, Shield } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { validatePassword } from '../../../../utils/passwordValidation';
import PasswordRequirements from '../../../../components/PasswordRequirements';

const KioskSignup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    fullName: '',
    username: '',
    password: '',
    confirmPassword: '',
    language: 'en'
  });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [passwordValidation, setPasswordValidation] = useState(null);
  const [tokenValidation, setTokenValidation] = useState({
    isValidating: true,
    isValid: false,
    error: null
  });
  const [kioskToken, setKioskToken] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'https://ksp3plhq.com/api';

  // Validate kiosk token on component mount
  useEffect(() => {
    const validateToken = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setTokenValidation({
          isValidating: false,
          isValid: false,
          error: 'No access token provided. Please scan the QR code from the kiosk.'
        });
        return;
      }

      try {
        const response = await fetch(`${API_URL}/kiosk-tokens/validate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ token })
        });

        const data = await response.json();

        if (data.valid) {
          setTokenValidation({
            isValidating: false,
            isValid: true,
            error: null
          });
          setKioskToken(token);
        } else {
          setTokenValidation({
            isValidating: false,
            isValid: false,
            error: data.message || 'Invalid or expired access token.'
          });
        }
      } catch (err) {
        console.error('Error validating token:', err);
        setTokenValidation({
          isValidating: false,
          isValid: false,
          error: 'Failed to validate access token. Please try again.'
        });
      }
    };

    validateToken();
  }, [searchParams, API_URL]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    const newFormData = {
      ...formData,
      [name]: value
    };
    
    setFormData(newFormData);
    
    // Clear error when field changes
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
    
    // Update password validation when password changes
    if (name === 'password') {
      if (value) {
        setPasswordValidation(validatePassword(value, 'lmsuser'));
      } else {
        setPasswordValidation(null);
      }
    }
    
    // Clear confirm password error when either password field changes
    if (name === 'password' || name === 'confirmPassword') {
      if (formErrors.confirmPassword) {
        setFormErrors({
          ...formErrors,
          confirmPassword: null
        });
      }
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Full name validation
    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required';
    } else if (formData.fullName.length < 2) {
      errors.fullName = 'Full name must be at least 2 characters';
    } else if (formData.fullName.length > 100) {
      errors.fullName = 'Full name must be less than 100 characters';
    }
    
    // Username validation
    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    } else if (formData.username.length > 50) {
      errors.username = 'Username must be less than 50 characters';
    } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
      errors.username = 'Username can only contain letters, numbers, hyphens and underscores';
    }
    
    // Password validation
    if (!formData.password) {
      errors.password = 'Password is required';
    } else {
      const validation = validatePassword(formData.password, 'lmsuser');
      if (!validation.isValid) {
        errors.password = validation.message;
      }
    }
    
    // Confirm password validation
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    // Language validation
    if (!formData.language) {
      errors.language = 'Language is required';
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setLoading(true);
    setSubmitStatus(null);
    
    try {
      // Prepare data for API
      const userData = {
        username: formData.username,
        email: `${formData.username}@lmsuser.local`, // Auto-generate email for LMS users
        password: formData.password,
        fullName: formData.fullName,
        phoneNumber: null,
        role: 'lmsuser',
        language: formData.language,
        timezone: 'CST',
        isActive: 1,
        forcePasswordReset: 0,
        softeonUsername: null,
        lvUsername: null,
        locusUsername: null
      };
      
      // Add the kiosk token to the request for validation
      const requestData = {
        ...userData,
        kioskToken: kioskToken
      };

      const response = await fetch(`${API_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Check for specific error types
        if (response.status === 409 || errorData.message?.includes('already exists')) {
          throw new Error('Username already exists. Please choose a different username.');
        }
        
        throw new Error(errorData.message || 'Failed to create user account');
      }
      
      // Success
      setSubmitStatus('success');
      
      // Redirect to kiosk view after 2 seconds
      setTimeout(() => {
        navigate('/tiles/labor_management_system/kiosk/kiosk-view');
      }, 2000);
      
    } catch (err) {
      console.error('Error creating user:', err);
      setSubmitStatus('error');
      setFormErrors({
        ...formErrors,
        general: err.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/tiles/labor_management_system/kiosk/kiosk-view');
  };

  // If token is still being validated, show loading
  if (tokenValidation.isValidating) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-slate-800 rounded-lg shadow-xl overflow-hidden">
            <div className="bg-slate-700 p-6 text-center">
              <Shield className="w-16 h-16 text-white mx-auto mb-3" />
              <h1 className="text-2xl font-bold text-white">Validating Access</h1>
              <p className="text-slate-300 mt-2">Please wait while we verify your access token...</p>
            </div>
            <div className="p-6 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If token is invalid, show error and redirect option
  if (!tokenValidation.isValid) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-slate-800 rounded-lg shadow-xl overflow-hidden">
            <div className="bg-red-700 p-6 text-center">
              <AlertCircle className="w-16 h-16 text-white mx-auto mb-3" />
              <h1 className="text-2xl font-bold text-white">Access Denied</h1>
              <p className="text-red-100 mt-2">Unable to access the sign-up page</p>
            </div>
            <div className="p-6">
              <div className="bg-red-900/30 border border-red-800 text-red-300 px-4 py-3 rounded-lg mb-6">
                <p className="font-semibold">Access Error</p>
                <p className="text-sm mt-1">{tokenValidation.error}</p>
              </div>
              <div className="text-center space-y-4">
                <p className="text-slate-300 text-sm">
                  To sign up for an account, please scan the QR code displayed on the employee kiosk.
                </p>
                <button
                  onClick={() => navigate('/tiles/labor_management_system/kiosk/kiosk-view')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                >
                  Go to Kiosk
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-slate-800 rounded-lg shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-slate-700 p-6 text-center">
            <UserPlus className="w-16 h-16 text-white mx-auto mb-3" />
            <h1 className="text-2xl font-bold text-white">Employee Sign Up</h1>
            <p className="text-slate-300 mt-2">Create your account to use the time clock</p>
            <div className="mt-3 flex items-center justify-center text-green-200 text-sm">
              <Shield className="w-4 h-4 mr-1" />
              Verified Access Token
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Status messages */}
            {submitStatus === 'success' && (
              <div className="p-4 bg-green-900/30 border border-green-800 text-green-300 rounded-lg flex items-center">
                <Check className="w-5 h-5 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-semibold">Account created successfully!</p>
                  <p className="text-sm mt-1">Redirecting to clock in screen...</p>
                </div>
              </div>
            )}
            
            {submitStatus === 'error' && formErrors.general && (
              <div className="p-4 bg-red-900/30 border border-red-800 text-red-300 rounded-lg flex items-start">
                <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">Sign up failed</p>
                  <p className="text-sm mt-1">{formErrors.general}</p>
                </div>
              </div>
            )}

            {/* Full Name */}
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-slate-300 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 bg-slate-700 border ${
                  formErrors.fullName ? 'border-red-500' : 'border-slate-600'
                } rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                placeholder="Enter your full name"
                disabled={loading || submitStatus === 'success'}
              />
              {formErrors.fullName && (
                <p className="mt-1 text-sm text-red-400">{formErrors.fullName}</p>
              )}
            </div>

            {/* Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-slate-300 mb-2">
                Username *
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 bg-slate-700 border ${
                  formErrors.username ? 'border-red-500' : 'border-slate-600'
                } rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                placeholder="Choose a username"
                disabled={loading || submitStatus === 'success'}
              />
              {formErrors.username && (
                <p className="mt-1 text-sm text-red-400">{formErrors.username}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-2">
                Password *
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 bg-slate-700 border ${
                  formErrors.password ? 'border-red-500' : (passwordValidation?.isValid ? 'border-green-500' : 'border-slate-600')
                } rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                placeholder="Create a password"
                disabled={loading || submitStatus === 'success'}
              />
              {formErrors.password && (
                <p className="mt-1 text-sm text-red-400">{formErrors.password}</p>
              )}
              
              {/* Password requirements */}
              {formData.password && (
                <PasswordRequirements 
                  role="lmsuser" 
                  validationState={passwordValidation} 
                />
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-300 mb-2">
                Confirm Password *
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 bg-slate-700 border ${
                  formErrors.confirmPassword ? 'border-red-500' : 'border-slate-600'
                } rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                placeholder="Confirm your password"
                disabled={loading || submitStatus === 'success'}
              />
              {formErrors.confirmPassword && (
                <p className="mt-1 text-sm text-red-400">{formErrors.confirmPassword}</p>
              )}
            </div>

            {/* Language */}
            <div>
              <label htmlFor="language" className="block text-sm font-medium text-slate-300 mb-2">
                Preferred Language *
              </label>
              <select
                id="language"
                name="language"
                value={formData.language}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 bg-slate-700 border ${
                  formErrors.language ? 'border-red-500' : 'border-slate-600'
                } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                disabled={loading || submitStatus === 'success'}
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="hm">Hmong</option>
              </select>
              {formErrors.language && (
                <p className="mt-1 text-sm text-red-400">{formErrors.language}</p>
              )}
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 bg-slate-600 hover:bg-slate-500 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center"
                disabled={loading || submitStatus === 'success'}
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </button>
              <button
                type="submit"
                className={`flex-1 font-bold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center ${
                  loading || submitStatus === 'success' 
                    ? 'bg-gray-600 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
                disabled={
                  loading || 
                  submitStatus === 'success' || 
                  !formData.fullName.trim() ||
                  (formData.password && passwordValidation && !passwordValidation.isValid)
                }
              >
                {loading ? (
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : submitStatus === 'success' ? (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Success!
                  </>
                ) : (
                  <>
                    <UserPlus className="w-5 h-5 mr-2" />
                    Sign Up
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default KioskSignup;